import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { NO_COMPARISON, variation } from "../../utils/analyticsDeltas";
import { percentOf, previousTotalOf, rateChange, seriesChange } from "../../utils/analyticsSeries";
import { capabilitiesOf, shownOnly } from "../../utils/analyticsCapabilities";
import { roundedShare } from "../../utils/analyticsSentences";
import RankedBars from "../charts/RankedBars";
import { formatCount, formatPercent } from "../charts/format";
import BreakdownChart from "./BreakdownChart";
import EvolutionCard from "./EvolutionCard";
import KpiStrip from "./KpiStrip";
import { openRateKpi, previousAndPerDayText } from "./kpis";
import PagesCard from "./PagesCard";
import Section from "./Section";
import Segmented from "./Segmented";
import StepFunnel from "./StepFunnel";

const ADOPTION_METRICS = [
  { key: "page_views", label: "analyticsPageViews", better: "up" },
  { key: "opens", label: "analyticsOpens", better: "up" },
  { key: "invites_shown", label: "analyticsInvitesShown", better: "up", needs: "invites" },
  { key: "invite_opens", label: "analyticsInvitesOpens", better: "up", needs: "invites" },
];

// Num periodo de chat embutido o assistente esta sempre aberto na pagina e o total de aberturas do backend
// so conta as de clientes: como na visao geral (R8), a faixa deixa as aberturas e a taxa de abertura, e
// fica com as visualizacoes e a taxa dos convites. Esta, embutido ou nao, so aparece num chat com
// convites. A taxa de abertura e a media por dia vem do `kpis.js`.
const AdoptionStrip = ({ summary, previousComplete }) => {
  const { translate, language } = useLanguage();
  const { headline, series, sources, funnel, chat } = summary;
  const m = series.metrics;
  const count = (value) => formatCount(value, language);
  const views = headline.page_views;
  const invites = sources.invites;

  const kpis = [
    {
      key: "page_views",
      label: translate("analyticsPageViews"),
      value: count(views.value),
      change: previousComplete ? variation({ kind: "count", ...views, better: "up" }, language) : NO_COMPARISON,
      spark: [m.page_views.current, m.page_views.previous],
      context: translate("analyticsKpiPrevious", { previous: count(views.previous) }),
    },
    // As aberturas so se mostram fora de um periodo embutido, onde o `funnel.opens` e um numero.
    ...(chat.docked ? [] : [
      {
        key: "opens",
        label: translate("analyticsOpens"),
        value: count(funnel.opens),
        change: seriesChange({ series, key: "opens", better: "up", previousComplete, language }),
        spark: [m.opens.current, m.opens.previous],
        context: previousAndPerDayText({ previous: previousTotalOf(series, "opens"), total: funnel.opens, series, translate, language }),
      },
      openRateKpi({ summary, previousComplete, translate, language }),
    ]),
    {
      key: "invites_rate",
      needs: "invites",
      label: translate("analyticsInvitesRate"),
      value: invites.open_rate_pct === null ? translate("analyticsNotAvailable") : formatPercent(invites.open_rate_pct, language),
      change: rateChange({ series, part: "invite_opens", whole: "invites_shown", better: "up", previousComplete, language }),
      spark: [percentOf(m.invite_opens.current, m.invites_shown.current), percentOf(m.invite_opens.previous, m.invites_shown.previous)],
      context: translate("analyticsKpiInvitesShown", { shown: count(invites.shown), count: invites.shown }),
    },
  ];
  return <KpiStrip label={translate("analyticsHeadline")} kpis={shownOnly(kpis, capabilitiesOf(summary))} />;
};

// Os convites como funil (com zero convites mostra zeros, de proposito): mostrados, e a taxa de abertura do backend entre este passo e as aberturas.
const InvitesFunnel = ({ invites }) => {
  const { translate, language } = useLanguage();
  const steps = [
    { key: "shown", label: translate("analyticsInvitesShown"), value: invites.shown },
    {
      key: "opens",
      label: translate("analyticsInvitesOpens"),
      value: invites.opens,
      rate: invites.open_rate_pct === null ? null : formatPercent(invites.open_rate_pct, language),
      rateLabel: translate("analyticsFunnelRate.opens"),
    },
  ];
  return <StepFunnel title={translate("analyticsInvites")} steps={steps} />;
};

const AUDIENCES = ["page_views", "conversations"];

const DevicesCard = ({ devices, which }) => {
  const { translate } = useLanguage();
  const [audience, setAudience] = useState("page_views");
  const title = translate(which === "device" ? "analyticsDeviceTitle" : "analyticsSystemTitle");
  const breakdown = devices[audience][which];
  const downloadName = `${title} ${translate(`analyticsAudience.${audience}`)}`;
  const choice = (
    <Segmented
      label={title}
      options={AUDIENCES.map((value) => ({ value, label: translate(`analyticsAudience.${value}`) }))}
      value={audience}
      onChange={setAudience}
    />
  );
  // O dispositivo tem tres valores e fica na barra empilhada; o sistema tem mais e vai por ordem (R7).
  if (which === "device") {
    return <BreakdownChart title={title} info={translate("analyticsDevicesNote")} breakdown={breakdown} dimension="device" actions={choice} downloadName={downloadName} />;
  }
  return <RankedBars title={title} info={translate("analyticsDevicesNote")} breakdown={breakdown} dimension="os" actions={choice} downloadName={downloadName} />;
};

// O separador Adocao (spec do redesign, R7): quem abre e de onde. A faixa de numeros, de onde abrem (com os
// convites num funil, num chat que os tem), as paginas com mais conversas (que vem do /pages, pedido pela
// pagina, D8 do plano das paginas), a evolucao e quem sao. Dispositivo e sistema num cartao cada, com a
// escolha entre visualizacoes e conversas (eram quatro cartoes quase iguais).
const AdoptionTab = ({ summary, period, today, previousComplete, pages, pagesError, onRetryPages }) => {
  const { translate } = useLanguage();
  const { sources, devices } = summary;
  const capabilities = capabilitiesOf(summary);
  const launcherShare = summary.chat.docked ? null : roundedShare(sources.opens.values.launcher || 0, sources.opens.total);
  return (
    <div className="space-y-6">
      <AdoptionStrip summary={summary} previousComplete={previousComplete} />
      <Section
        title={translate("analyticsWhereTheyOpen")}
        note={launcherShare === null ? null : translate("analyticsOpensLead", { share: launcherShare })}
        className={capabilities.invites ? "grid gap-4 lg:grid-cols-2 items-start" : "grid gap-4"}
      >
        <RankedBars title={translate("analyticsOpensBySource")} breakdown={sources.opens} dimension="source" />
        {capabilities.invites && <InvitesFunnel invites={sources.invites} />}
      </Section>
      <Section title={translate("analyticsPagesTitle")} className="grid gap-4">
        <PagesCard pages={pages} error={pagesError} onRetry={onRetryPages} />
      </Section>
      <Section title={translate("analyticsEvolution")} className="grid gap-4">
        <EvolutionCard
          series={summary.series}
          metrics={shownOnly(ADOPTION_METRICS, capabilities)}
          title={translate(summary.series.granularity === "hour" ? "analyticsHourByHour" : "analyticsDayByDay")}
          partialLast={period.to === today}
          previousComplete={previousComplete}
          cut={summary.cut}
          timeZone={period.tz}
        />
      </Section>
      <Section title={translate("analyticsWhoTheyAre")} className="grid gap-4 lg:grid-cols-2 items-start">
        <DevicesCard devices={devices} which="device" />
        <DevicesCard devices={devices} which="os" />
        <div className="lg:col-span-2">
          <RankedBars title={translate("analyticsConversationsLanguage")} breakdown={devices.conversations.language} dimension="language" />
        </div>
      </Section>
    </div>
  );
};

export default AdoptionTab;
